import { forwardRef, useContext } from 'react';

import Chip from '@mui/material/Chip';
import classNames from 'classnames';

import { Box } from '@vk-hr-tek/ui/Box';
import { Paper } from '@vk-hr-tek/ui/Paper';
import { Link } from '@vk-hr-tek/ui/Link';
import { MenuItem } from '@vk-hr-tek/ui/MenuItem';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { DropdownButton } from '@vk-hr-tek/ui/DropdownButton';
import {
  useOverflowedElements,
  useShadowRootElement,
} from '@vk-hr-tek/ui/hooks';
import { useTranslations } from '@vk-hr-tek/core/hooks';
import { PortalContext } from '@vk-hr-tek/ui/Portal';

import { modules } from '@app/modules';

import { useUrlWithPrefix } from '../../../hooks';

import { useStyles } from './GroupFilter.styles';
import { ListBoxCategory } from './ListBox';
import { SearchGroupFilter, LoadItemsFilter } from './SearchInput.types';

const DROPDOWN_WIDTH = 32;
const LINK_WIDTH = 142;
const CONTAINER_OFFSET = 36;
const CHIPS_GAP = 8;
const WIDTH_BUFFER = 40;

interface GroupFilterProps {
  categories: ListBoxCategory[];
  filter: LoadItemsFilter;
  setFilter: (value: LoadItemsFilter) => void;
  show: boolean;
  showResult: boolean;
  children?: React.ReactNode | React.ReactNode[];
  onClose: () => void;
  onDropdownOpen: () => void;
  onDropdownClose: () => void;
  dropdownWidth: number;
}

export const GroupFilter = forwardRef<HTMLDivElement, GroupFilterProps>(
  (
    {
      categories,
      filter,
      setFilter,
      showResult,
      show,
      children,
      onClose,
      onDropdownOpen,
      onDropdownClose,
      dropdownWidth,
      ...rest
    },
    ref,
  ) => {
    const { classes } = useStyles();
    const urlWithPrefix = useUrlWithPrefix();
    const { t } = useTranslations();

    const portalContext = useContext(PortalContext);
    const menuContainer = useShadowRootElement(
      portalContext?.portalId
        ? `portal-container-${portalContext.portalId}`
        : undefined,
    );

    const elementsIds = categories.map((c) => String(c.value));

    const chipsGapTotal = CHIPS_GAP * categories.length;
    const containerWidth =
      dropdownWidth -
      LINK_WIDTH -
      DROPDOWN_WIDTH -
      CONTAINER_OFFSET -
      chipsGapTotal -
      WIDTH_BUFFER;

    const { visibleElements, hiddenElements, setElementRef } =
      useOverflowedElements({
        elementsIds,
        containerWidth,
        dependencies: dropdownWidth,
      });

    const selectedFromHidden = filter.group
      ? categories.find(
          (c) =>
            c.value === filter.group &&
            hiddenElements.includes(String(c.value)),
        )
      : null;

    const lastVisibleCategory = selectedFromHidden
      ? categories
          .filter((c) => visibleElements.includes(String(c.value)))
          .at(-1)
      : null;

    const getVisibleCategories = () => {
      const visible = categories.filter((c) =>
        visibleElements.includes(String(c.value)),
      );
      if (selectedFromHidden && lastVisibleCategory) {
        return [
          ...visible.filter((c) => c.value !== lastVisibleCategory.value),
          selectedFromHidden,
        ];
      }
      return visible;
    };

    const getHiddenCategories = () => {
      const hidden = categories.filter((c) =>
        hiddenElements.includes(String(c.value)),
      );
      if (selectedFromHidden && lastVisibleCategory) {
        return [
          ...hidden.filter((c) => c.value !== selectedFromHidden.value),
          lastVisibleCategory,
        ];
      }
      return hidden;
    };

    const visibleCategories = getVisibleCategories();
    const hiddenCategories = getHiddenCategories();

    const hrefGlobalSearch = `/${modules.search.basename}`;

    const preventBlur = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
    };

    const handleChangeCategory = (value: SearchGroupFilter['value']) => {
      setFilter({
        ...filter,
        group: value || undefined,
      });
    };

    if (!show) {
      return null;
    }

    return (
      <Box visibility={containerWidth < 0 ? 'hidden' : 'visible'}>
        <Paper {...rest} ref={ref} onMouseDown={preventBlur}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="16"
            borderBottom={showResult ? 1 : 0}
            borderColor="stroke.primary"
            p="16"
          >
            <Box
              display="flex"
              justifyContent="start"
              alignItems="center"
              gap="8"
            >
              {categories.length === 1 ? (
                <Typography variant="body3" color="text.light.secondary">
                  {categories[0].value === 'employees' &&
                    t('search.searchOnlyBy.employees')}
                  {categories[0].value === 'surveys' &&
                    t('search.searchOnlyBy.surveys')}
                </Typography>
              ) : (
                <>
                  {visibleCategories.map((category) => {
                    const isActive = filter.group === category.value;

                    return (
                      <Box
                        key={category.value}
                        ref={setElementRef(String(category.value))}
                      >
                        <Chip
                          label={category.label}
                          variant="outlined"
                          clickable
                          onClick={() => {
                            handleChangeCategory(
                              category.value as SearchGroupFilter['value'],
                            );
                          }}
                          className={classNames(
                            classes.chip,
                            isActive && classes.active,
                          )}
                        />
                      </Box>
                    );
                  })}
                  {hiddenCategories.length > 0 && (
                    <DropdownButton
                      variant="quaternary"
                      verticalPosition="bottom"
                      horizontalPosition="left"
                      container={menuContainer}
                      onOpen={onDropdownOpen}
                      onClose={onDropdownClose}
                      menuItems={
                        <Box display="flex" flexDirection="column">
                          {hiddenCategories.map((category) => (
                            <MenuItem
                              key={category.value}
                              onClick={() => {
                                handleChangeCategory(
                                  category.value as SearchGroupFilter['value'],
                                );
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body3"
                                  color="text.light.primary"
                                >
                                  {category.label}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Box>
                      }
                    />
                  )}
                </>
              )}
            </Box>
            <Box
              display="flex"
              justifyContent="end"
              alignItems="center"
              minWidth={LINK_WIDTH}
            >
              <Link
                {...urlWithPrefix(hrefGlobalSearch)}
                stroke={false}
                size="small"
                variant="secondary"
                display="block"
                onClick={onClose}
              >
                Расширенный поиск
              </Link>
            </Box>
          </Box>
          {showResult && children}
        </Paper>
      </Box>
    );
  },
);
